import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFormValidation, formValidationMethods } from '@pattern-library/base';
import { Button, FlexButtonGroup } from '@pattern-library/buttons';
import PhoneInput from '@pattern-library/phone-input';

const { matchesPattern } = formValidationMethods;

const propTypes = {
    formAction: PropTypes.string.isRequired,
    antiForgeryToken: PropTypes.string.isRequired,
    callButtonProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
        .isRequired,
    textButtonProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object]))
        .isRequired,
    phoneInputProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
        .isRequired
};

const Format = {
    US: 'US',
    INTL: 'INTL'
};

function ConfirmPhoneNumberForm(props) {
    const { formAction, antiForgeryToken, callButtonProps, textButtonProps, phoneInputProps } =
        props;

    const [format, setFormat] = useState(Format.US);

    /**
     * Run the given `validationFn` if the phone number is a US number.
     * Otherwise, return true.
     */
    function ifUS(validationFn) {
        return value => {
            return format === Format.US ? validationFn(value) : true;
        };
    }

    /**
     * Run the given `validationFn` if the phone number is an international number.
     * Otherwise, return true.
     */
    function ifInternational(validationFn) {
        return value => {
            return format === Format.INTL ? validationFn(value) : true;
        };
    }

    const schema = {
        [phoneInputProps.name]: [
            {
                validationMethod: 'required',
                errorMessage: 'Enter a phone number'
            },
            {
                validationMethod: ifUS(value => matchesPattern(value, /^\(\d{3}\) \d{3}-\d{4}$/)),
                errorMessage: 'Phone number must be 10 digits'
            },
            {
                validationMethod: ifInternational(value => !matchesPattern(value, /^\+\d$/)),
                errorMessage: 'Phone number must be longer than one digit'
            },
            {
                validationMethod: ifInternational(value => matchesPattern(value, /\d \d/)),
                errorMessage: 'Add a space after country code'
            }
        ]
    };
    const { hasError, errorMessage, onSubmit } = useFormValidation({ schema });

    return (
        <form action={formAction} method="post" onSubmit={onSubmit} autoComplete="off" noValidate>
            <div dangerouslySetInnerHTML={{ __html: antiForgeryToken }} />
            <PhoneInput
                {...phoneInputProps}
                onChange={(_, countryCode) => {
                    if (countryCode === '1') {
                        setFormat(Format.US);
                    } else {
                        setFormat(Format.INTL);
                    }
                }}
                international
                internationalFormatOptions={{ allowSpaceAfterCountryCode: true }}
                isRequired
                subtext={format === Format.INTL ? 'Please add a space after Country code' : null}
                hasError={hasError(phoneInputProps.name)}
                errorMessage={errorMessage(phoneInputProps.name)}
            />

            <FlexButtonGroup alignment="center" className="asc-mt-4 asc-mb-2">
                <Button
                    type="submit"
                    className="asc-mtb-0 asc-btn--secondary"
                    {...callButtonProps}
                />
                <Button
                    type="submit"
                    className="asc-mtb-0"
                    {...textButtonProps}
                    customAttrs={{
                        ...textButtonProps.customAttrs,
                        'aria-describedby': 'text-fees-notice'
                    }}
                />
            </FlexButtonGroup>

            <p className="asc-p--footnote asc-text--center asc-mtb-0" id="text-fees-notice">
                Standard text messaging fees may apply.
            </p>
        </form>
    );
}

ConfirmPhoneNumberForm.propTypes = propTypes;
export default ConfirmPhoneNumberForm;
